import {
  endOfWeek,
  format,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  parse
} from 'date-fns';
import { ko } from 'date-fns/locale';

export { ko as LOCALE_KO };
export const FORMAT_DATE = 'yyyy-MM-dd';
export const FORMAT_DATE_TIME = 'yyyy-MM-dd HH:mm:ss';
export const FORMAT_DATE_DOT = 'yyyy.MM.dd';

export const ToDateTimeLong = (d: Date | null, locale: 'ko' | 'en' = 'ko') => {
  if (!d) {
    return '';
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'long'
  }).format(d);
};

export const ToDateTimeFull = (d: Date) => {
  const date = new Intl.DateTimeFormat('ko', {
    dateStyle: 'long',
    timeStyle: 'long'
  })
    .format(d)
    .split(' ');
  date.pop();
  return date.join(' ');
};

export const DateFormat = (d: Date | null, fmt: string = FORMAT_DATE) => {
  if (!d) return '';
  return format(d, fmt);
};

export const DateFullFormat = (d: Date | null) => {
  if (!d) return null;
  return format(d, FORMAT_DATE_TIME);
};

export const ParseDate = (d: string, fmt: string = FORMAT_DATE) => {
  if (!d) return null;
  return parse(d, fmt, new Date(), { locale: ko });
};

export const ParseDateAndThrow = (d: string, fmt: string = FORMAT_DATE) => {
  if (!d) throw new Error();
  return ParseDate(d, fmt);
};

export function DiffDateMMSS(start: Date, end: Date) {
  if (!start && !end) {
    return '';
  }
  const startAt = start as any;
  const endAt = end as any;
  const diffMs = Math.abs(endAt - startAt);
  const secs = Math.floor(Math.abs(diffMs) / 1000);
  const mins = Math.floor(secs / 60);
  return `${String(mins).padStart(2, '0')}:${String(secs - mins * 60).padStart(
    2,
    '0'
  )}`;
}

export function DateMMSSBySeconds(secs: number) {
  if (!secs) {
    return 0;
  }
  const mins = Math.floor(secs / 60);
  return `${String(mins).padStart(2, '0')}:${String(secs - mins * 60).padStart(
    2,
    '0'
  )}`;
}

// ── 하이드레이션-안전 날짜 표시 (#274) ────────────────────
// 서버(컨테이너 UTC)와 클라이언트(KST)가 항상 같은 문자열을 내도록
// 타임존을 Asia/Seoul 로 고정한다. 자정 부근 날짜 어긋남 방지.
export function formatDateKST(
  date: string | Date,
  month: 'long' | 'short' = 'long'
) {
  return new Date(date).toLocaleDateString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month,
    day: 'numeric'
  });
}

// now 를 인자로 받는 순수 함수. 렌더 중 Date.now() 를 부르면 SSR 과
// 하이드레이션 산출물이 갈릴 수 있어(React #418) 호출자가 시각을 넘긴다.
// 렌더 안에서는 이 함수를 직접 쓰지 말고 <TimeAgo> 컴포넌트를 쓴다.
export function timeAgo(date: string | Date, now: number) {
  const diff = now - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '방금 전';
  if (mins < 60) return `${mins}분 전`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}일 전`;
  return formatDateKST(date);
}

export function getStartOfWeek(date: Date) {
  return startOfWeek(date, { weekStartsOn: 1, locale: ko });
}

export function getEndOfWeek(date: Date) {
  return endOfWeek(date, { weekStartsOn: 1, locale: ko });
}

export function firstVisibleDay(date: Date) {
  return startOfWeek(startOfMonth(date));
}

export function lastVisibleDay(date: Date) {
  return endOfWeek(endOfMonth(date));
}
